import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ResponsiveBar } from '@nivo/bar';
import { ResponsiveLine } from '@nivo/line';
import {
  getTotalMinutes,
  getWeekRecords,
  getYearRecords,
  mapWeeklyTotals,
  mapMonthlyTotals,
} from '../utils/statsUtils';
import { ReadingRecord } from '../types/records';
import { useApi, apiClient } from '../hooks/useApi';
import BottomNav from '../components/BottomNav';
import { formatMinutesToHuman } from '../utils/formatUtils';

type Tab = 'week' | 'year';

const StatCard = ({ label, value, sub }: { label: string; value: string | number; sub?: string }) => (
  <div className="bg-parchment-100 rounded-xl border border-parchment-200 shadow-sm p-4 text-center">
    <p className="text-2xl font-bold text-leather">{value}</p>
    <p className="text-xs font-semibold text-ink-muted mt-1">{label}</p>
    {sub && <p className="text-xs text-ink-muted mt-0.5">{sub}</p>}
  </div>
);

const StatsPage = () => {
  const navigate = useNavigate();
  const { getSessions } = useApi();
  const [records, setRecords] = useState<ReadingRecord[]>([]);
  const [currentStreak, setCurrentStreak] = useState(0);
  const [longestStreak, setLongestStreak] = useState(0);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<Tab>('week');

  useEffect(() => {
    Promise.all([
      getSessions(),
      apiClient.getStreak().catch(() => ({ currentStreak: 0, longestStreak: 0, lastReadDate: null })),
    ])
      .then(([sessionData, streakData]: [any[], any]) => {
        const mapped: ReadingRecord[] = sessionData.map((s) => ({
          date: s.createdAt,
          minutes: s.actualTimeSpent ?? 0,
          book: s.bookRead ?? '',
          chapters: s.chaptersRead ?? [],
        }));
        setRecords(mapped);
        setCurrentStreak(streakData.currentStreak ?? 0);
        setLongestStreak(streakData.longestStreak ?? 0);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const currentYear = new Date().getFullYear();
  const weekRecords = getWeekRecords(records);
  const yearRecords = getYearRecords(records, currentYear);
  const totalMinutes = getTotalMinutes(records);
  const weekMinutes = getTotalMinutes(weekRecords);

  const weeklyData = mapWeeklyTotals(weekRecords);
  const monthlyData = mapMonthlyTotals(yearRecords);

  const weekBarData = weeklyData.map((d) => ({ day: d.day, Minutes: d.minutes }));
  const monthBarData = monthlyData.map((d) => ({ month: d.month, Minutes: d.minutes }));

  const last7Days = Array.from({ length: 7 }, (_, i) => {
    const d = new Date();
    d.setDate(d.getDate() - (6 - i));
    d.setHours(0, 0, 0, 0);
    return d;
  });
  const lineData = [
    {
      id: 'Minutes',
      data: last7Days.map((d) => {
        const dayRecords = records.filter((r) => new Date(r.date).toDateString() === d.toDateString());
        return {
          x: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][d.getDay()],
          y: dayRecords.reduce((s, r) => s + r.minutes, 0),
        };
      }),
    },
  ];

  return (
    <div className="min-h-screen bg-parchment-50 pb-20">
      {/* Header */}
      <div className="bg-parchment-100 border-b border-parchment-200 px-4 py-4">
        <div className="max-w-2xl mx-auto flex items-center gap-2">
          <span className="text-2xl">📖</span>
          <h1 className="text-xl font-bold text-ink">TrackBible</h1>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        <h2 className="text-2xl font-bold text-ink">Stats</h2>

        {/* Stat cards */}
        <div className="grid grid-cols-2 gap-3">
          <StatCard
            label="This Week"
            value={weekMinutes > 0 ? formatMinutesToHuman(weekMinutes) : '—'}
          />
          <StatCard
            label="All Time"
            value={totalMinutes > 0 ? formatMinutesToHuman(totalMinutes) : '—'}
          />
          <StatCard label="Current Streak" value={currentStreak} sub="days" />
          <StatCard label="Longest Streak" value={longestStreak} sub="days" />
        </div>

        <div>
          <div className="flex border-b border-parchment-300 mb-4">
            {(['week', 'year'] as Tab[]).map((t) => (
              <button
                key={t}
                onClick={() => setTab(t)}
                className={`px-5 py-2 text-sm font-semibold capitalize transition ${
                  tab === t
                    ? 'border-b-2 border-leather text-leather'
                    : 'text-ink-muted hover:text-ink'
                }`}
              >
                {t === 'week' ? 'This Week' : 'This Year'}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-40">
              <div className="w-8 h-8 border-4 border-leather border-t-transparent rounded-full animate-spin" />
            </div>
          ) : records.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center gap-4">
              <span className="text-6xl">📖</span>
              <h3 className="text-xl font-semibold text-ink-light">No sessions yet</h3>
              <p className="text-ink-muted max-w-xs">
                Start your first Bible reading session to see your progress here.
              </p>
              <button
                onClick={() => navigate('/app')}
                className="mt-2 bg-leather hover:bg-leather-light text-parchment-100 px-6 py-3 rounded-lg font-semibold transition"
              >
                Start Reading
              </button>
            </div>
          ) : tab === 'week' ? (
            <div className="space-y-6">
              <div className="bg-parchment-100 rounded-xl border border-parchment-200 shadow-sm p-4">
                <h3 className="text-sm font-semibold text-ink-muted mb-3">Minutes per Day</h3>
                <div className="h-48">
                  <ResponsiveLine
                    data={lineData}
                    margin={{ top: 10, right: 20, bottom: 30, left: 40 }}
                    xScale={{ type: 'point' }}
                    yScale={{ type: 'linear', min: 0, max: 'auto' }}
                    curve="monotoneX"
                    colors={['#5c4a34']}
                    pointSize={8}
                    pointColor="#fdfaf3"
                    pointBorderWidth={2}
                    pointBorderColor={{ from: 'serieColor' }}
                    enableArea
                    areaOpacity={0.15}
                    axisBottom={{ tickSize: 0, tickPadding: 8 }}
                    axisLeft={{ tickSize: 0, tickPadding: 8, tickValues: 4 }}
                    enableGridX={false}
                    gridYValues={4}
                    theme={{
                      axis: { ticks: { text: { fontSize: 11, fill: '#7a6e5e' } } },
                      grid: { line: { stroke: '#edd9a3' } },
                    }}
                  />
                </div>
              </div>

              <div className="bg-parchment-100 rounded-xl border border-parchment-200 shadow-sm p-4">
                <h3 className="text-sm font-semibold text-ink-muted mb-3">Sessions by Day</h3>
                <div className="h-40">
                  <ResponsiveBar
                    data={weekBarData}
                    keys={['Minutes']}
                    indexBy="day"
                    colors={['#5c4a34']}
                    margin={{ top: 5, right: 10, bottom: 30, left: 40 }}
                    padding={0.3}
                    borderRadius={4}
                    axisBottom={{ tickSize: 0, tickPadding: 8 }}
                    axisLeft={{ tickSize: 0, tickPadding: 8, tickValues: 4 }}
                    enableLabel={false}
                    enableGridX={false}
                    gridYValues={4}
                    theme={{
                      axis: { ticks: { text: { fontSize: 11, fill: '#7a6e5e' } } },
                      grid: { line: { stroke: '#edd9a3' } },
                    }}
                  />
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="bg-parchment-100 rounded-xl border border-parchment-200 shadow-sm p-4">
                <h3 className="text-sm font-semibold text-ink-muted mb-3">
                  Minutes per Month — {currentYear}
                </h3>
                <div className="h-48">
                  <ResponsiveBar
                    data={monthBarData}
                    keys={['Minutes']}
                    indexBy="month"
                    colors={['#5c4a34']}
                    margin={{ top: 5, right: 10, bottom: 30, left: 40 }}
                    padding={0.3}
                    borderRadius={4}
                    axisBottom={{ tickSize: 0, tickPadding: 8 }}
                    axisLeft={{ tickSize: 0, tickPadding: 8, tickValues: 4 }}
                    enableLabel={false}
                    enableGridX={false}
                    gridYValues={4}
                    theme={{
                      axis: { ticks: { text: { fontSize: 11, fill: '#7a6e5e' } } },
                      grid: { line: { stroke: '#edd9a3' } },
                    }}
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <StatCard label="Sessions" value={yearRecords.length} />
                <StatCard
                  label="Total Hours"
                  value={`${Math.floor(getTotalMinutes(yearRecords) / 60)}h`}
                />
                <StatCard
                  label="Active Months"
                  value={new Set(yearRecords.map((r) => new Date(r.date).getMonth())).size}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      <BottomNav />
    </div>
  );
};

export default StatsPage;
